// Lean imports — money math/validation needs codes + decimal digits only;
// the full ./currencies records (names, flags, …) are picker data and must
// stay out of client bundles that use `exponentOf`/`Money`.
import {
  CURRENCY_FORMAT_DATA,
  LEAN_CURRENCY_CODES as CURRENCY_CODES,
  isSupportedCurrencyLean as isSupportedCurrency,
} from "../currency/currency-format-data";
import { DomainError } from "./domain-error";

/** Postgres `integer` (int4) range. Every minor-units column this app writes
 *  through SQL is cast `::integer`, so a rescale that overflows it fails
 *  there with 22003. `rescaleMinor` has no such column backing it — its two
 *  callers write into JSONB (`experience_prices.tiers`, a checkout-settings
 *  fee blob) — so it enforces the same bound itself, rather than silently
 *  writing a number neither SQL nor the app's own `Money`/DTO layer could
 *  ever have produced. */
export const MINOR_UNITS_INT4_MAX = 2_147_483_647;
export const MINOR_UNITS_INT4_MIN = -2_147_483_648;

export class MinorUnitRangeError extends DomainError {
  constructor(readonly amountMinor: number) {
    super(
      `Rescaled amount ${amountMinor} is outside the storable integer range`,
    );
    this.name = "MinorUnitRangeError";
  }
}

/** ISO 4217 currency codes accepted across TripCart — every active currency in
 *  the shared currency dataset (the single source of truth). Lower-case input is
 *  normalised to upper-case by `moneyDtoSchema` and `Money.of`. */
export const ALLOWED_CURRENCIES: readonly string[] = CURRENCY_CODES;

export type AllowedCurrency = string;

/** Per-currency minor-unit exponent (number of decimal places), sourced from the
 *  currency dataset (e.g. JPY=0, USD=2, BHD=3). Unknown codes default to 2. */
export function exponentOf(currencyCode: string): number {
  return CURRENCY_FORMAT_DATA[currencyCode.toUpperCase()]?.[1] ?? 2;
}

/**
 * Re-expresses a stored minor-units integer under a different exponent, for
 * the one moment the "relabel, no FX" money model needs a real conversion:
 * an agency switching its base currency between two currencies with
 * different decimal-place counts (e.g. USD's 2 -> JPY's 0). Every other read
 * of a minor-units column treats the agency's currency as a pure label over
 * the same integer — this is the single place that integer is allowed to
 * change, and only by the power-of-ten the exponent difference implies.
 */
export function rescaleMinor(
  amountMinor: number,
  fromExponent: number,
  toExponent: number,
): number {
  if (fromExponent === toExponent) return amountMinor;
  const rescaled = Math.round(amountMinor * 10 ** (toExponent - fromExponent));
  if (rescaled > MINOR_UNITS_INT4_MAX || rescaled < MINOR_UNITS_INT4_MIN) {
    throw new MinorUnitRangeError(rescaled);
  }
  return rescaled;
}

export class Money {
  constructor(
    readonly amount: number,
    readonly currencyCode: string,
  ) {
    if (!Number.isInteger(amount)) {
      throw new Error(
        `Money amount must be an integer (minor units), got: ${amount}`,
      );
    }
  }

  static of(amount: number, currencyCode: string): Money {
    const code = currencyCode.toUpperCase();
    if (!isSupportedCurrency(code)) {
      throw new Error(`Unsupported currency: ${currencyCode}`);
    }
    return new Money(amount, code);
  }

  add(other: Money): Money {
    if (other.currencyCode !== this.currencyCode) {
      throw new Error(
        `Currency mismatch: ${this.currencyCode} vs ${other.currencyCode}`,
      );
    }
    return new Money(this.amount + other.amount, this.currencyCode);
  }

  multiply(n: number): Money {
    return new Money(Math.round(this.amount * n), this.currencyCode);
  }

  equals(other: Money): boolean {
    return (
      this.amount === other.amount && this.currencyCode === other.currencyCode
    );
  }

  static exponentOf(currencyCode: string): number {
    return exponentOf(currencyCode);
  }
}
